import React, { FC, useEffect, useMemo, useState } from 'react';
import { Empty } from '@maxtropy/components';
import HlsVideo from './HlsVideo';
import HKPlayerFrame from './HKPlayerFrame';
import RtspPlayerFrame from './RtspPlayerFrame';
import { AccessMode, ConfigType } from './type';
import { isRtspUrl } from './RtspPlayerFrame/rtsp';
import { getEzvizTranscodeUrl, isEzopenUrl, shouldResolveEzvizUrl } from './EzvizProxyVideo/ezviz';
import styles from './index.module.scss';

export interface RecorderPreviewItem {
  id?: number;
  deviceId?: string;
  deviceName?: string;
  videoPlayUrl?: string;
  liveUrlTranscode?: string;
  supervisoryUrl?: string;
  rtspAddress?: string;
  thirdCode?: string;
  accessMode?: number;
  accountAccessMode?: number;
  configType?: number;
  monitorLocation?: string;
  cameraUsername?: string;
  cameraPassword?: string;
  ipAddress?: string;
  port?: number;
  cameraPort?: number;
  rtspPort?: number;
  enableFeature?: string;
  channelIndex?: number;
}

interface RecorderPreviewProps {
  item: RecorderPreviewItem;
  hkDomPrefix: string;
  videoObjectFit?: React.CSSProperties['objectFit'];
  videoBackgroundColor?: React.CSSProperties['backgroundColor'];
}

const getPreviewUrl = (item: RecorderPreviewItem) =>
  item.videoPlayUrl || item.liveUrlTranscode || item.supervisoryUrl || item.rtspAddress;

const RecorderPreview: FC<RecorderPreviewProps> = ({ item, hkDomPrefix, videoObjectFit, videoBackgroundColor }) => {
  const [playUrl, setPlayUrl] = useState<string>();
  const videoUrl = getPreviewUrl(item);
  const isPlatformAccess = item.accessMode === 1;
  const canPlayHK =
    !!item.ipAddress && !!item.port && !!item.cameraUsername && !!item.cameraPassword && !!item.rtspPort;
  const isPlatformHK =
    isPlatformAccess && (item.accountAccessMode === AccessMode.HK || (!item.accountAccessMode && canPlayHK));
  const isPlatformRtsp =
    (isPlatformAccess && item.accountAccessMode === AccessMode.RTSP) ||
    item.accessMode === AccessMode.RTSP ||
    (item.accountAccessMode == null && isRtspUrl(videoUrl));
  const isPlatformYsy =
    isPlatformAccess &&
    (item.accountAccessMode === AccessMode.YSY ||
      (item.accountAccessMode == null && (item.configType === ConfigType.PROXY || isEzopenUrl(videoUrl) || !videoUrl)));
  const hkDomId = useMemo(
    () => `${hkDomPrefix}_${item.id ?? item.deviceId ?? item.deviceName ?? 'unknown'}`,
    [hkDomPrefix, item.deviceId, item.deviceName, item.id]
  );

  useEffect(() => {
    let ignore = false;

    if (isPlatformHK || isPlatformRtsp) {
      setPlayUrl(videoUrl);
      return () => {
        ignore = true;
      };
    }

    const needResolveEzvizUrl =
      isPlatformYsy &&
      shouldResolveEzvizUrl({
        accessMode: AccessMode.YSY,
        configType: item.configType,
        url: videoUrl,
        deviceId: item.deviceId ?? item.id,
      });

    if (needResolveEzvizUrl) {
      getEzvizTranscodeUrl(item.deviceId ?? item.id)
        .then(url => {
          if (!ignore) {
            setPlayUrl(url);
          }
        })
        .catch(() => {
          if (!ignore) {
            setPlayUrl(undefined);
          }
        });
      return () => {
        ignore = true;
      };
    }

    if (isPlatformYsy && (item.configType === ConfigType.PROXY || !videoUrl || isEzopenUrl(videoUrl))) {
      setPlayUrl(undefined);
      return () => {
        ignore = true;
      };
    }

    setPlayUrl(videoUrl);
    return () => {
      ignore = true;
    };
  }, [isPlatformHK, isPlatformRtsp, isPlatformYsy, item.configType, item.deviceId, item.id, videoUrl]);

  if (isPlatformHK) {
    if (!canPlayHK) {
      return (
        <div className={styles.previewEmpty}>
          <Empty description="暂无视频" />
        </div>
      );
    }

    return (
      <HKPlayerFrame
        domId={hkDomId}
        monitorLocation={item.monitorLocation}
        cameraUsername={item.cameraUsername}
        cameraPassword={item.cameraPassword}
        ipAddress={item.ipAddress}
        port={item.port}
        cameraPort={item.cameraPort}
        rtspPort={item.rtspPort}
        channelIndex={item.channelIndex}
        enableFeature={item.enableFeature}
      />
    );
  }

  if (isPlatformRtsp) {
    return (
      <RtspPlayerFrame
        rtspUrl={playUrl ?? videoUrl}
        width="100%"
        height="100%"
        objectFit={videoObjectFit}
        backgroundColor={videoBackgroundColor}
      />
    );
  }

  if (playUrl) {
    return (
      <HlsVideo
        src={playUrl}
        code={item.thirdCode}
        height="100%"
        minHeight={0}
        objectFit={videoObjectFit}
        backgroundColor={videoBackgroundColor}
      />
    );
  }

  return (
    <div className={styles.previewEmpty}>
      <Empty description="暂无视频" />
    </div>
  );
};

export default RecorderPreview;
export { default as HlsVideo } from './HlsVideo';
export { default as HKPlayerFrame } from './HKPlayerFrame';
export { default as RtspPlayerFrame } from './RtspPlayerFrame';
export { default as EzvizProxyVideo } from './EzvizProxyVideo';
export * from './type';
export * from './RtspPlayerFrame/rtsp';
export * from './EzvizProxyVideo/ezviz';
